import { FormState, useDeleteSSOConnectionMutation, useGetSSOConnectionDetailsQuery } from "@circuitry-ai/doc-data";
import { Breadcrumb, BreadcrumbItem, checkType, CtyButton, CtyShowToast, ErrorBoundary, useAgGridStatePersistence, SimpleDeleteConfirmationModal, useHasApplicationResource } from "@circuitry-ai/doc-ui";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import SSOConfiguration from "./ssosetting";
import { useLocation, useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner, Tooltip } from '@nextui-org/react';

const BreadcrumbWithErrorBoundary = ErrorBoundary(Breadcrumb as unknown as React.ComponentType<Record<string, unknown>>);
const SSOConfigurationWithErrorBoundary = ErrorBoundary(SSOConfiguration);

interface SSOConnectionRow {
    connector_name: string;
    description?: string;
    connection_id?: string;
    login_url?: string;
    logout_url?: string;
    group_mapping?: Array<Record<string, string>>;
    [key: string]: unknown;
}

function parseGroupRows(connection: SSOConnectionRow | null): { group: string; value: string }[] {
    if (!connection?.group_mapping?.length) return [];
    return connection.group_mapping.map((mapping) => {
        const [group, value] = Object.entries(mapping)[0] ?? ['', ''];
        return { group, value: String(value ?? '') };
    });
}

const SSOConnectionsList = () => {
    const { t } = useTranslation();
    const hasSsoAccess = useHasApplicationResource('sso');
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const formState = params.get('state');

    const [currentDeletingIndex, setCurrentDeletingIndex] = useState<number>(-1);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewingConnection, setViewingConnection] = useState<SSOConnectionRow | null>(null);
    const [deleteSSOConnection, { isLoading: isDeletingConnection }] = useDeleteSSOConnectionMutation();
    const { data: ssoConnectionDetails, error: isSSOError, isLoading: isSSOConnectionLoading, isFetching: isSSOConnectionFetching, refetch: refetchSSOConnectionsList } = useGetSSOConnectionDetailsQuery(undefined, { refetchOnMountOrArgChange: true });
    const isLoadingSSOConnectionDetails = isSSOConnectionLoading || isSSOConnectionFetching;

    const handleUpdateConnection = useCallback((index: number) => {
        if (!ssoConnectionDetails?.[index]) return;
        navigate(`/settings/sso?state=${FormState.UPDATE}`, { state: { ssoConnectionDetails: ssoConnectionDetails[index] } });
    }, [navigate, ssoConnectionDetails]);

    const handleCreateConnection = useCallback(() => {
        navigate(`/settings/sso?state=${FormState.CREATE}`);
    }, [navigate]);

    const openViewModal = useCallback((index: number) => {
        if (!ssoConnectionDetails?.[index]) return;
        setViewingConnection(ssoConnectionDetails[index] as SSOConnectionRow);
        setViewModalOpen(true);
    }, [ssoConnectionDetails]);

    const closeViewModal = useCallback(() => {
        setViewModalOpen(false);
        setViewingConnection(null);
    }, []);

    const openDeleteModal = useCallback((index: number) => {
        if (!ssoConnectionDetails?.[index]) return;
        setPendingDeleteIndex(index);
        setDeleteModalOpen(true);
    }, [ssoConnectionDetails]);

    const closeDeleteModal = useCallback(() => {
        setDeleteModalOpen(false);
        setPendingDeleteIndex(null);
    }, []);

    const confirmConnectionDelete = useCallback(async () => {
        if (pendingDeleteIndex == null || !ssoConnectionDetails?.[pendingDeleteIndex]) return;
        const index = pendingDeleteIndex;
        setCurrentDeletingIndex(index);
        try {
            const response = await deleteSSOConnection(ssoConnectionDetails[index].connector_name).unwrap();
            CtyShowToast(checkType.success, response?.message);
            await refetchSSOConnectionsList();
            closeDeleteModal();
        } catch (error) {
            CtyShowToast(checkType.error, `${t('Failed to delete Connection details')}: ${(error as Error)?.message || 'Something went wrong!'}`);
        } finally {
            setCurrentDeletingIndex(-1);
        }
    }, [pendingDeleteIndex, deleteSSOConnection, ssoConnectionDetails, refetchSSOConnectionsList, t, closeDeleteModal]);

    const rowData = useMemo(() => (ssoConnectionDetails ?? []) as SSOConnectionRow[], [ssoConnectionDetails]);

    const gridStyle = useMemo(() => ({
        width: '100%',
        '--ag-border-color': '#e5e7eb',
        '--ag-cell-horizontal-border': '1px solid var(--ag-border-color)',
    } as React.CSSProperties), []);

    const paginationPageSizeSelector = useMemo(() => [10, 20, 40, 50], []);

    const defaultColDef = useMemo(() => ({ resizable: true }), []);

    const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } =
        useAgGridStatePersistence('sso-connections-table');

    const onGridReady = useCallback((params: GridReadyEvent) => {
        augmentGridReady(params, (p) => {
            p?.api?.sizeColumnsToFit?.();
        });
    }, [augmentGridReady]);

    const actionsCellRenderer = useCallback((params: { data: SSOConnectionRow; rowIndex: number }) => {
        const index = params.rowIndex;
        const isThisRowDeleting = isDeletingConnection && currentDeletingIndex === index;
        return (
            <div className="flex items-center justify-end gap-2 h-full py-1">
                <Tooltip content={t('View details')} placement="top">
                    <button
                        type="button"
                        className="cursor-pointer text-default-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => { e.stopPropagation(); openViewModal(index); }}
                        aria-label={t('View details')}
                        disabled={!hasSsoAccess}
                    >
                        <Icon icon="tabler:eye" width="18" height="18" />
                    </button>
                </Tooltip>
                <Tooltip content={t('Edit')} placement="top">
                    <button
                        type="button"
                        className="cursor-pointer text-default-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => { e.stopPropagation(); handleUpdateConnection(index); }}
                        aria-label={t('Edit')}
                        disabled={!hasSsoAccess}
                    >
                        <Icon icon="tabler:edit" width="18" height="18" />
                    </button>
                </Tooltip>
                <Tooltip content={t('Delete')} placement="top">
                    <button
                        type="button"
                        className="cursor-pointer text-default-500 hover:text-danger relative inline-flex items-center min-w-[18px] min-h-[18px] disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => { e.stopPropagation(); openDeleteModal(index); }}
                        aria-label={t('Delete')}
                        disabled={!hasSsoAccess}
                    >
                        {isThisRowDeleting ? (
                            <Spinner size="sm" />
                        ) : (
                            <Icon icon="hugeicons:delete-02" width="18" height="18" />
                        )}
                    </button>
                </Tooltip>
            </div>
        );
    }, [handleUpdateConnection, openViewModal, openDeleteModal, isDeletingConnection, currentDeletingIndex, t, hasSsoAccess]);

    const columnDefs = useMemo<ColDef[]>(() => [
        {
            headerName: t('Connection Name'),
            field: 'connector_name',
            sortable: true,
            flex: 1,
            minWidth: 200,
        },
        {
            headerName: t('Status'),
            field: 'status',
            sortable: false,
            minWidth: 120,
            cellRenderer: () => (
                <span className="inline-flex items-center gap-1.5 text-success">
                    <Icon icon="solar:check-circle-bold" width="18" height="18" />
                    {t('Active')}
                </span>
            ),
        },
        {
            headerName: t('Actions'),
            field: 'actions',
            sortable: false,
            maxWidth: 120,
            resizable: false,
            pinned: 'right',
            lockPinned: true,
            lockPosition: true,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'start' },
            cellRenderer: (params: { data: SSOConnectionRow; rowIndex: number }) => actionsCellRenderer(params),
        },
    ], [t, actionsCellRenderer]);

    const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);

    const breadCrumbsList: BreadcrumbItem[] = useMemo(() => {
        const list: BreadcrumbItem[] = [{ label: 'SSO', path: '/settings/sso', active: true }];
        if (formState) {
            list.push({ label: formState, path: `/settings/sso?state=${formState}`, active: false });
        }
        return list;
    }, [formState]);
    const isShowBreadcrumbEnabled = breadCrumbsList.length > 1;
    const isShowConnectionListEnabled = formState === null;
    const isShowConfigurationFormEnabled = formState !== null;

    const renderTable = () => {
        if (isLoadingSSOConnectionDetails) {
            return (
                <div className="flex justify-center items-center min-h-[200px]">
                    <Spinner size="lg" />
                </div>
            );
        }
        if (isSSOError) {
            return (
                <div className="text-danger text-sm py-4">{t('Failed to load SSO connections')}</div>
            );
        }
        return (
            <div className="ag-theme-alpine mt-4" style={gridStyle}>
                <AgGridReact
                    suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
                    rowData={rowData}
                    columnDefs={columnDefsWithSort}
                    defaultColDef={defaultColDef}
                    headerHeight={40}
                    domLayout="autoHeight"
                    onGridReady={onGridReady}
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={paginationPageSizeSelector}
                    alwaysShowHorizontalScroll={true}
                    suppressRowClickSelection={true}
                />
            </div>
        );
    };

    return (
        <div className={`flex flex-col h-full pt-2`}>
            {isShowBreadcrumbEnabled && <BreadcrumbWithErrorBoundary crumbs={breadCrumbsList} separator=">" />}
            {isShowConnectionListEnabled && (
                <>
                    <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-2">
                        <div className="flex flex-col gap-1">
                            <h1 className="font-poppins text-2xl font-semibold leading-8 text-foreground">{t('Authentication')}</h1>
                            <p className="font-poppins text-sm font-normal leading-5 text-default-600">{t('Configure Single Sign-On connections for your workspace.')}</p>
                        </div>
                        <CtyButton
                            id="create-connection-btn"
                            color="primary"
                            size="md"
                            className="shrink-0 font-semibold"
                            onPress={handleCreateConnection}
                            startContent={<Icon icon="fa6-solid:plus" width="16" height="16" />}
                            isDisabled={!hasSsoAccess}
                        >
                            {t('Add Connection')}
                        </CtyButton>
                    </header>
                    {renderTable()}
                    <SimpleDeleteConfirmationModal
                        isOpen={deleteModalOpen}
                        onOpenChange={(open) => {
                            if (!open) closeDeleteModal();
                        }}
                        entityName={
                            pendingDeleteIndex == null
                                ? ''
                                : ssoConnectionDetails?.[pendingDeleteIndex]?.connector_name ?? ''
                        }
                        onConfirm={confirmConnectionDelete}
                        isConfirmLoading={isDeletingConnection}
                        cancelLabel={t('Close')}
                        confirmLabel={t('Delete Connection')}
                    />
                    <Modal
                        isOpen={viewModalOpen}
                        onOpenChange={(open) => {
                            if (!open) closeViewModal();
                        }}
                        size="3xl"
                        scrollBehavior="inside"
                    >
                        <ModalContent>
                            {(onClose) => {
                                const viewGroupRows = parseGroupRows(viewingConnection);
                                return (
                                <>
                                    <ModalHeader className="flex flex-col gap-1">
                                        {t('Connection details')}
                                        {viewingConnection?.connector_name ? (
                                            <span className="text-sm font-normal text-default-500">
                                                {viewingConnection.connector_name}
                                            </span>
                                        ) : null}
                                    </ModalHeader>
                                    <ModalBody className="gap-4">
                                        <div className="flex flex-col gap-4">
                                            <div className="flex gap-8 items-start">
                                                <span className="w-[25%] shrink-0 text-default-500 text-sm">
                                                    {t('Connection Name')}
                                                </span>
                                                <p className="flex-1 text-sm text-foreground break-words m-0">
                                                    {viewingConnection?.connector_name ?? '—'}
                                                </p>
                                            </div>
                                            <div className="flex gap-8 items-start">
                                                <span className="w-[25%] shrink-0 text-default-500 text-sm pt-1">
                                                    {t('Login URL')}
                                                </span>
                                                <p className="text-sm text-foreground break-all m-0 flex-1 min-w-0">
                                                    {viewingConnection?.login_url?.trim() ? viewingConnection.login_url : '—'}
                                                </p>
                                            </div>
                                            <div className="flex gap-8 items-start">
                                                <span className="w-[25%] shrink-0 text-default-500 text-sm pt-1">
                                                    {t('Logout URL')}
                                                </span>
                                                <p className="text-sm text-foreground break-all m-0 flex-1 min-w-0">
                                                    {viewingConnection?.logout_url?.trim() ? viewingConnection.logout_url : '—'}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <span className="text-sm text-default-500">{t('Groups')}</span>
                                                {viewGroupRows.length === 0 ? (
                                                    <p className="text-sm text-default-400 m-0">—</p>
                                                ) : (
                                                    <div className="flex flex-col gap-0 border border-default-200 rounded-medium overflow-hidden">
                                                        <div className="grid grid-cols-2 gap-4 px-3 py-2 bg-default-100 text-xs text-default-500 font-medium border-b border-default-200">
                                                            <span>{t('Group')}</span>
                                                            <span>{t('Value')}</span>
                                                        </div>
                                                        {viewGroupRows.map(({ group, value }, rowIdx) => (
                                                            <div
                                                                key={`${group}-${rowIdx}`}
                                                                className="grid grid-cols-2 gap-4 px-3 py-2.5 text-sm text-foreground border-b border-default-100 last:border-b-0"
                                                            >
                                                                <span className="break-words">{group}</span>
                                                                <span className="break-words">{value}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </ModalBody>
                                    <ModalFooter>
                                        <CtyButton color="primary" variant="flat" onPress={onClose}>
                                            {t('Close')}
                                        </CtyButton>
                                    </ModalFooter>
                                </>
                                );
                            }}
                        </ModalContent>
                    </Modal>
                </>
            )}
            {isShowConfigurationFormEnabled && <SSOConfigurationWithErrorBoundary refetchConnectionList={refetchSSOConnectionsList} />}
        </div>
    );
};

export default SSOConnectionsList;
